import React, { useState, useEffect } from "react";
import { useQuery, useMutation } from "@apollo/client";
import {
  GET_BRANCHES,
  GET_TABLES,
  ADD_TABLE,
  UPDATE_TABLE,
  DELETE_TABLE,
} from "../queries";

interface TableItem {
  id?: number;
  name: string;
  branch_id: number;
}

const Tables: React.FC = () => {
  const [selectedBranch, setSelectedBranch] = useState<number | null>(null);
  const [tables, setTables] = useState<TableItem[]>([]);
  const [saving, setSaving] = useState(false);
  const [alertMessage, setAlertMessage] = useState<{
    type: "success" | "danger";
    message: string;
  } | null>(null);

  const { data: branchesData } = useQuery(GET_BRANCHES);
  const {
    data: tablesData,
    loading,
    refetch,
  } = useQuery(GET_TABLES, {
    variables: { branch: selectedBranch ? parseInt(String(selectedBranch)) : null },
    skip: !selectedBranch,
  });

  const [addTable] = useMutation(ADD_TABLE);
  const [updateTable] = useMutation(UPDATE_TABLE);
  const [deleteTable] = useMutation(DELETE_TABLE);

  useEffect(() => {
    if (branchesData?.getBranches?.length && !selectedBranch) {
      setSelectedBranch(branchesData.getBranches[0].id);
    }
  }, [branchesData, selectedBranch]);

  useEffect(() => {
    setTables(tablesData?.getTables || []);
  }, [tablesData, selectedBranch]);

  const handleAddTable = () => {
    if (!selectedBranch) return;
    setTables([...tables, { name: "", branch_id: selectedBranch }]);
  };

  const handleUpdateName = (index: number, name: string) => {
    const updated = [...tables];
    updated[index] = { ...updated[index], name };
    setTables(updated);
  };

  const handleDelete = async (index: number) => {
    const table = tables[index];
    if (table.id) {
      try {
        await deleteTable({ variables: { id: parseInt(String(table.id)) } });
        await refetch();
        setAlertMessage({
          type: "success",
          message: "Mesa eliminada exitosamente",
        });
      } catch (error) {
        console.error("Error deleting table:", error);
        setAlertMessage({
          type: "danger",
          message: "Error al eliminar la mesa",
        });
      }
    } else {
      setTables(tables.filter((_, i) => i !== index));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      for (const table of tables) {
        if (!table.name.trim()) continue;
        if (table.id) {
          await updateTable({
            variables: { id: parseInt(String(table.id)), name: table.name.trim() },
          });
        } else {
          await addTable({
            variables: {
              name: table.name.trim(),
              branch_id: parseInt(String(selectedBranch)),
            },
          });
        }
      }
      await refetch();
      setAlertMessage({
        type: "success",
        message: "Mesas guardadas exitosamente",
      });
    } catch (error) {
      console.error("Error saving tables:", error);
      setAlertMessage({
        type: "danger",
        message: "Error al guardar las mesas",
      });
    }
    setSaving(false);
  };

  const clearAlert = () => setAlertMessage(null);

  return (
    <div className="row justify-content-start mt-3">
      <div className="col col-md-8">
        <div className="card mb-2">
          {alertMessage && (
            <div
              className={`alert alert-${alertMessage.type} alert-dismissible fade show m-3`}
              role="alert"
            >
              {alertMessage.message}
              <button
                type="button"
                className="close"
                onClick={clearAlert}
                aria-label="Close"
              >
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
          )}
          <div className="card-body">
            <h4>Mesas</h4>
            <p className="text-muted">
              Administra las mesas de cada sucursal para el punto de venta.
            </p>

            <div className="form-group">
              <label>Sucursal</label>
              <select
                className="form-control"
                value={selectedBranch || ""}
                onChange={(e) => setSelectedBranch(parseInt(e.target.value))}
              >
                {branchesData?.getBranches?.map((branch: any) => (
                  <option key={branch.id} value={branch.id}>
                    {branch.name || branch.address}
                  </option>
                ))}
              </select>
            </div>

            <div className="mt-3">
              {loading ? (
                <div className="text-center py-3">
                  <i className="fas fa-spinner fa-spin"></i> Cargando...
                </div>
              ) : (
                <>
                  {tables.map((table, index) => (
                    <div key={table.id || `new-${index}`} className="form-row mb-2">
                      <div className="col-9">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Nombre de la mesa"
                          maxLength={10}
                          value={table.name || ""}
                          onChange={(e) =>
                            handleUpdateName(index, e.target.value)
                          }
                        />
                        <small className={`form-text ${(table.name || "").length >= 10 ? "text-danger" : "text-muted"}`}>
                          {(table.name || "").length}/10 caracteres
                        </small>
                      </div>
                      <div className="col-3">
                        <button
                          type="button"
                          className="btn p-0 py-1 m-0"
                          onClick={() => handleDelete(index)}
                        >
                          <i className="far fa-minus-square fa-2x text-danger"></i>
                        </button>
                      </div>
                    </div>
                  ))}
                  <div className="form-row">
                    <div className="col-12">
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm"
                        onClick={handleAddTable}
                      >
                        <i className="fas fa-plus"></i> Agregar Mesa
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>

            <button
              type="button"
              className="btn btn-success btn-block mt-4"
              onClick={handleSave}
              disabled={saving || loading}
            >
              {saving ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i> Guardando...
                </>
              ) : (
                "Guardar"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Tables;
